import { Pipe, PipeTransform } from '@angular/core';
import { BankReconciliationBank, DayRange } from '@app/interface/bank-reconciliation';

const WEEKDAYS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
const MONTHS = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

/** `R$ 1.250,00`; negativo vira `− R$ 954,80` (com `showPlus`, positivo ganha `+`). */
export function formatCents(cents: number | null | undefined, showPlus = false): string {
  if (cents === null || cents === undefined) return '—';
  const text = BRL.format(Math.abs(cents) / 100).replace(/ /g, ' ');
  if (cents < 0) return `− ${text}`;
  return showPlus && cents > 0 ? `+ ${text}` : text;
}

/** `YYYY-MM-DD` → `DD/MM/YYYY` (sem passar por `Date`, para não deslocar o dia por fuso). */
export function formatDay(day: string | null | undefined): string {
  if (!day || !/^\d{4}-\d{2}-\d{2}/.test(day)) return day ?? '—';
  return `${day.slice(8, 10)}/${day.slice(5, 7)}/${day.slice(0, 4)}`;
}

export function formatDayShort(day: string | null | undefined): string {
  if (!day || !/^\d{4}-\d{2}-\d{2}/.test(day)) return day ?? '—';
  return `${day.slice(8, 10)}/${day.slice(5, 7)}`;
}

export function weekdayOf(day: string): string {
  const [year, month, date] = day.split('-').map(Number);
  return WEEKDAYS[new Date(year, month - 1, date).getDay()] ?? '';
}

/** `2026-09` → `setembro de 2026`. */
export function formatMonth(month: string): string {
  const [year, monthNumber] = month.split('-').map(Number);
  return `${MONTHS[monthNumber - 1]} de ${year}`;
}

export function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function shiftMonth(month: string, delta: number): string {
  const [year, monthNumber] = month.split('-').map(Number);
  const date = new Date(year, monthNumber - 1 + delta, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/** Último dia do mês (`2026-09` → `2026-09-30`). */
export function monthEnd(month: string): string {
  const [year, monthNumber] = month.split('-').map(Number);
  return `${month}-${String(new Date(year, monthNumber, 0).getDate()).padStart(2, '0')}`;
}

/** Dia `YYYY-MM-DD` → `YYYY-MM` do mês. */
export function monthOfDay(day: string): string {
  return day.slice(0, 7);
}

/** Hoje (dia local) em `YYYY-MM-DD`. */
export function todayDay(): string {
  const now = new Date();
  return `${currentMonth()}-${String(now.getDate()).padStart(2, '0')}`;
}

/** O período livre da API aceita no máximo 366 dias por consulta. */
export const MAX_RANGE_DAYS = 366;

/** Dias do intervalo, contando os dois extremos. */
export function daysInRange(range: DayRange): number {
  const [fromYear, fromMonth, fromDay] = range.from.split('-').map(Number);
  const [toYear, toMonth, toDay] = range.to.split('-').map(Number);
  return Math.round((Date.UTC(toYear, toMonth - 1, toDay) - Date.UTC(fromYear, fromMonth - 1, fromDay)) / 86_400_000) + 1;
}

/** O mês inteiro como intervalo (`2026-09` → `2026-09-01` a `2026-09-30`). */
export function monthRange(month: string): DayRange {
  return { from: `${month}-01`, to: monthEnd(month) };
}

/** `YYYY-MM` quando o intervalo é exatamente um mês de calendário; `null` nos demais. */
export function fullMonthOf(range: DayRange): string | null {
  const month = range.from.slice(0, 7);
  return range.from === `${month}-01` && range.to === monthEnd(month) ? month : null;
}

/** "setembro de 2026" para o mês inteiro; "10/09/2026" para um dia; "01/09/2026 a 15/09/2026" nos demais. */
export function formatRange(range: DayRange): string {
  const month = fullMonthOf(range);
  if (month) return formatMonth(month);
  return range.from === range.to ? formatDay(range.from) : `${formatDay(range.from)} a ${formatDay(range.to)}`;
}

/** Primeiro dia do mês atual (padrão da data de corte). */
export function firstDayOfCurrentMonth(): string {
  return `${currentMonth()}-01`;
}

export function bankLabel(bank: BankReconciliationBank): string {
  const account = bank.account ? `CC ${bank.account}${bank.digito ? `-${bank.digito}` : ''}` : null;
  const name = bank.description || bank.name || 'Banco';
  return account ? `${name} — ${account}` : name;
}

export function bankSubtitle(bank: BankReconciliationBank): string {
  return [bank.name, bank.agencia ? `Ag. ${bank.agencia}` : null, bank.account ? `CC ${bank.account}${bank.digito ? `-${bank.digito}` : ''}` : null]
    .filter(Boolean)
    .join(' · ');
}

@Pipe({ name: 'brlCents', standalone: true })
export class BrlCentsPipe implements PipeTransform {
  transform(cents: number | null | undefined, showPlus = false): string {
    return formatCents(cents, showPlus);
  }
}

@Pipe({ name: 'bankDay', standalone: true })
export class BankDayPipe implements PipeTransform {
  transform(day: string | null | undefined, mode: 'full' | 'short' = 'full'): string {
    return mode === 'short' ? formatDayShort(day) : formatDay(day);
  }
}

@Pipe({ name: 'bankWeekday', standalone: true })
export class BankWeekdayPipe implements PipeTransform {
  transform(day: string | null | undefined): string {
    return day ? weekdayOf(day) : '';
  }
}

/** Abertura padrão dos diálogos da conciliação (casca em `_tp-dialog.scss`). */
export const TP_DIALOG_CONFIG = {
  panelClass: 'tp-dialog-panel',
  autoFocus: 'first-tabbable',
  restoreFocus: true,
} as const;
